/**
 * SalesReportBuilder.tsx — Sales / Reports → the Builder TAB (Phase 2 / 2.5,
 * §16.7, §20 B-1..B-11, §20.6). ONE question of ONE object: filters + a date
 * range × one group-by × one measure, rendered as a bar chart (when asked)
 * and always as a table. Nothing here computes: the server's closed token
 * table is the authority, every figure on screen is the wire's, and a
 * refused slug arrives as a named 400 that is shown verbatim (U-8).
 *
 *   §20.6 Q1  "Showing 200 of N groups …" is rendered ONLY when N > 200,
 *             from the server's exact groupCount; the totals row covers ALL
 *             groups and the line says so.
 *   B-4       countedRecords + excludedNonGbp columns appear beside every
 *             MONEY measure and only then (the count measure's money keys
 *             arrive null and render as no column, never as 0).
 *   B-5       group values are verbatim — "Outbound" and "outbound" stay two
 *             rows; the null group reads "(none)".
 *   B-11      five measures; "Σ fenced value" (the deals' own value) is the
 *             default money measure, "Σ forecast amount" beside it.
 *   Save      ui_saved_views, object sales.reports, PRIVATE (D-5): the whole
 *             definition is the view's filters.
 *   Export    the CSV route — uncapped — and the UI says, before the click,
 *             that every export is recorded (§20.6 Q2b).
 *
 * Filters offered are the vocabulary-backed ones (stage, priority, forecast
 * category, status, activity type), the exact-string source and — since §35
 * (ruled 2026-09-20) — the four id-valued ones through PICKERS: owner
 * (OwnerSelect with leavers included, "Any owner" = no filter), account
 * (AccountPicker, pick-only), contact and deal (ContactPicker / DealPicker on
 * the EntityPicker core). The id is the value that travels; the text is what
 * the picker showed and never leaves this screen. A saved view loaded with
 * an id resolves its name LIVE through the by-id reads; a 404 leaves the RAW
 * id in the control with a plain client-side note that the record is no
 * longer available (ruling 3) — nothing is fabricated, and the shared error
 * helper is untouched. NOT offered, deliberately: `forecastCategorySource`
 * (the wire accepts rep | override on deals; no control exists here — its
 * own queue row) and `assignedTo` (the unlinked text owner; allowlisted, no
 * control by ruling).
 */
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts'
import { X } from 'lucide-react'
import { toast } from 'sonner'
import { LoadingState, EmptyState } from '../../DashboardShared'
import { querySalesReport, exportSalesReportCSV, fetchSavedViews, createSavedView, fetchAccount, fetchContact, fetchDeal } from '../../api'
import { apiErrorMessage } from '../../../../api/client'
import type { SalesReportDefinition, SalesReportResult, SalesReportObject, SalesReportMeasure, SalesReportChart } from '../../../../api/types'
import type { SavedView } from './savedViews'
import { accent, ink, surface, shadow } from '../../../../theme/tokens'
import { money } from '../../../../lib/money'
import { moneyAxis } from './chartAxis'
import { FORECAST_CATEGORY_OPTIONS, PRIORITY_OPTIONS } from './vocab'
import { SLUG_TO_STAGE } from '../../api'
import { OwnerSelect } from './OwnerSelect'
import { AccountPicker } from './AccountPicker'
import { ContactPicker } from './ContactPicker'
import { DealPicker } from './DealPicker'
import {
  OBJECT_OPTIONS, DIMENSIONS, MEASURES, RANGE_FIELDS, defaultDefinition, withObject, isMoneyMeasure,
  measureLabel, dimensionLabel, groupLabel, truncationLine, measureCell, countedCell, excludedNote,
  chartRows, EXPORT_NOTE, exportedSentence, savedViewBody, definitionFromSaved,
  ID_FILTERS, ID_FILTER_RESOLVING, idFilterFailureNote, type ResolvableIdFilter,
} from './reportBuilder'

const inputCls = 'h-9 px-3 text-12 bg-surface-control border border-edge-control rounded-control text-ink-body placeholder:text-ink-faint focus:outline-none focus:border-accent'

const STAGE_CHIPS = Object.entries(SLUG_TO_STAGE).map(([value, label]) => ({ value, label }))
const CONTACT_STATUS_CHIPS = ['lead', 'contacted', 'qualified', 'unqualified', 'converted'].map(v => ({ value: v, label: v }))
const ACTIVITY_TYPE_CHIPS = ['note', 'call', 'meeting', 'email'].map(v => ({ value: v, label: v }))
const ACTIVITY_STATUS_CHIPS = ['pending', 'completed'].map(v => ({ value: v, label: v }))

function toggle(list: string[] | undefined, value: string): string[] | undefined {
  const cur = list ?? []
  const next = cur.includes(value) ? cur.filter(v => v !== value) : [...cur, value]
  return next.length ? next : undefined
}

/** §35: one id-valued filter's row — a label bound to its picker, the clear
 *  control while anything is in it, and the note under it (resolving, or
 *  the deleted-target sentence) when there is one. */
function IdFilter({ label, htmlFor, note, active, onClear, children }: {
  label: string; htmlFor: string; note?: string; active: boolean; onClear: () => void; children: ReactNode
}) {
  return (
    <span className="inline-flex flex-col gap-0.5" data-id-filter={htmlFor}>
      <span className="inline-flex items-center gap-2">
        <label htmlFor={htmlFor}>{label}</label>
        {children}
        {active && (
          <button type="button" onClick={onClear} aria-label={`Clear ${label.toLowerCase()} filter`} title={`Clear ${label.toLowerCase()} filter`}
            className="inline-flex items-center text-ink-faint hover:text-ink-primary transition-colors">
            <X size={12} />
          </button>
        )}
      </span>
      {note && <span className="text-11 text-ink-muted" data-id-filter-note>{note}</span>}
    </span>
  )
}

function Chips({ label, options, selected, onToggle }: { label: string; options: ReadonlyArray<{ value: string; label: string }>; selected?: string[]; onToggle: (v: string) => void }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label={label}>
      <span className="text-11 text-ink-muted uppercase tracking-wide mr-1">{label}</span>
      {options.map(o => {
        const on = selected?.includes(o.value) ?? false
        return (
          <button key={o.value} type="button" aria-pressed={on}
            className={`px-2 py-0.5 rounded-full text-11 border ${on ? 'bg-surface-nav-hover border-accent text-ink-primary' : 'border-edge-control text-ink-body'}`}
            onClick={() => onToggle(o.value)}>{o.label}</button>
        )
      })}
    </div>
  )
}

export function ReportBuilderTab() {
  const [def, setDef] = useState<SalesReportDefinition>(defaultDefinition)
  const [result, setResult] = useState<SalesReportResult | null>(null)
  const [running, setRunning] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [saveName, setSaveName] = useState('')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState<SavedView[]>([])
  // §35: what each id picker SHOWS (the picked or resolved name; the raw id
  // when it could not be resolved) and the note beside it. Display state
  // only — the definition carries the ids and nothing else.
  const [idText, setIdText] = useState<Partial<Record<ResolvableIdFilter, string>>>({})
  const [idNote, setIdNote] = useState<Partial<Record<ResolvableIdFilter, string>>>({})
  const loadSeq = useRef(0)

  useEffect(() => {
    let live = true
    fetchSavedViews('sales.reports').then(v => { if (live) setSaved(v) }).catch(() => { /* the list is a convenience; a failure leaves it empty */ })
    return () => { live = false }
  }, [])

  const set = (patch: Partial<SalesReportDefinition>) => setDef(d => ({ ...d, ...patch }))
  const setFilter = (patch: Partial<SalesReportDefinition['filters']>) => setDef(d => ({ ...d, filters: { ...d.filters, ...patch } }))

  /** A pick, a keystroke or a clear on one of the three resolvable id
   *  pickers: the id (or none) into the definition, the text into display. */
  function setIdFilter(key: ResolvableIdFilter, id: string, text: string) {
    setFilter({ [key]: id || undefined })
    setIdText(t => ({ ...t, [key]: text }))
    setIdNote(n => ({ ...n, [key]: undefined }))
  }

  /** A loaded view's id filters, resolved LIVE through the by-id reads (the
   *  owner needs none: OwnerSelect lists the directory, leavers included).
   *  Renamed → today's name; deleted → the raw id plus the plain note;
   *  any other failure → the raw id plus the helper's message. A later load
   *  supersedes an earlier one still in flight. */
  async function resolveIdFilters(loaded: SalesReportDefinition) {
    const seq = ++loadSeq.current
    const f = loaded.filters
    const pending: Array<{ key: ResolvableIdFilter; id: string; read: () => Promise<string> }> = []
    if (f.accountId) pending.push({ key: 'accountId', id: f.accountId, read: () => fetchAccount(f.accountId!).then(a => a.account.name) })
    if (f.contactId) pending.push({ key: 'contactId', id: f.contactId, read: () => fetchContact(f.contactId!).then(c => c.name) })
    if (f.dealId) pending.push({ key: 'dealId', id: f.dealId, read: () => fetchDeal(f.dealId!).then(d => d.title) })
    setIdText({})
    setIdNote(Object.fromEntries(pending.map(p => [p.key, ID_FILTER_RESOLVING])))
    if (pending.length === 0) return
    const settled = await Promise.all(pending.map(async p => {
      try {
        return { key: p.key, text: await p.read(), note: undefined as string | undefined }
      } catch (e) {
        return { key: p.key, text: p.id, note: idFilterFailureNote(p.key, e) }
      }
    }))
    if (seq !== loadSeq.current) return
    setIdText(Object.fromEntries(settled.map(s => [s.key, s.text])))
    setIdNote(Object.fromEntries(settled.filter(s => s.note).map(s => [s.key, s.note])))
  }

  async function run() {
    setRunning(true)
    try {
      setResult(await querySalesReport(def))
    } catch (e) {
      // A 400 names the slug the whitelist refused — shown verbatim.
      toast.error(apiErrorMessage(e, 'Failed to run the report'))
    } finally {
      setRunning(false)
    }
  }

  async function exportCSV() {
    setExporting(true)
    try {
      const csv = await exportSalesReportCSV(def)
      const url = URL.createObjectURL(csv.blob)
      const a = document.createElement('a')
      a.href = url
      a.download = csv.filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
      toast.success(exportedSentence(csv.rows, csv.excludedNonGbp))
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to export the report'))
    } finally {
      setExporting(false)
    }
  }

  async function save() {
    const name = saveName.trim()
    if (!name) return
    setSaving(true)
    try {
      const view = await createSavedView(savedViewBody(name, def))
      setSaved(list => [...list, view])
      setSaveName('')
      toast.success(`Saved "${name}" — private to you`)
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to save the report'))
    } finally {
      setSaving(false)
    }
  }

  function load(id: string) {
    const view = saved.find(v => v.id === id)
    if (!view) return
    const loaded = definitionFromSaved(view.filters)
    if (!loaded) {
      toast.error(`"${view.name}" was saved by a newer builder and cannot be read here`)
      return
    }
    setDef(loaded)
    setResult(null)
    void resolveIdFilters(loaded)
  }

  const object: SalesReportObject = def.object
  const money_ = isMoneyMeasure(def.measure)

  return (
    <div className="flex flex-col gap-4">
      {/* 1. The question. */}
      <section className="k-card" aria-label="Report definition">
        <div className="k-card-head">
          <div>
            <div className="k-card-title">Define the question</div>
            <div className="k-card-sub">One object, grouped one way, measured one way. The server decides what is askable; anything else is refused by name.</div>
          </div>
        </div>
        <div className="p-5 flex flex-col gap-4">
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1 text-11 text-ink-muted">Object
              <select value={object} aria-label="Object" className={`${inputCls} bg-surface-card`}
                onChange={e => { setDef(withObject(def, e.target.value as SalesReportObject)); setResult(null); setIdText({}); setIdNote({}) }}>
                {OBJECT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-11 text-ink-muted">Group by
              <select value={def.groupBy} aria-label="Group by" className={`${inputCls} bg-surface-card`}
                onChange={e => set({ groupBy: e.target.value })}>
                {DIMENSIONS[object].map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-11 text-ink-muted">Measure
              <select value={def.measure} aria-label="Measure" className={`${inputCls} bg-surface-card`}
                onChange={e => set({ measure: e.target.value as SalesReportMeasure })}>
                {MEASURES[object].map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
            <div className="flex flex-col gap-1 text-11 text-ink-muted">Chart
              <div className="k-segmented" role="group" aria-label="Chart">
                {(['bar', 'table'] as const).map(c => (
                  <button key={c} type="button" aria-pressed={def.chart === c} aria-selected={def.chart === c}
                    onClick={() => set({ chart: c as SalesReportChart })}>{c === 'bar' ? 'Bar + table' : 'Table only'}</button>
                ))}
              </div>
            </div>
          </div>

          {/* Filters — the vocabulary-backed ones, per object. */}
          <div className="flex flex-col gap-2" aria-label="Filters">
            {object === 'deals' && (
              <>
                <Chips label="Stage" options={STAGE_CHIPS} selected={def.filters.stage} onToggle={v => setFilter({ stage: toggle(def.filters.stage, v) })} />
                <Chips label="Priority" options={PRIORITY_OPTIONS} selected={def.filters.priority} onToggle={v => setFilter({ priority: toggle(def.filters.priority, v) })} />
                <Chips label="Forecast category" options={[...FORECAST_CATEGORY_OPTIONS, { value: 'uncategorised', label: 'Uncategorised' }]}
                  selected={def.filters.forecastCategory} onToggle={v => setFilter({ forecastCategory: toggle(def.filters.forecastCategory, v) })} />
              </>
            )}
            {object === 'contacts' && (
              <Chips label="Status" options={CONTACT_STATUS_CHIPS} selected={def.filters.status} onToggle={v => setFilter({ status: toggle(def.filters.status, v) })} />
            )}
            {object === 'activities' && (
              <>
                <Chips label="Type" options={ACTIVITY_TYPE_CHIPS} selected={def.filters.activityType} onToggle={v => setFilter({ activityType: toggle(def.filters.activityType, v) })} />
                <Chips label="Status" options={ACTIVITY_STATUS_CHIPS} selected={def.filters.status} onToggle={v => setFilter({ status: toggle(def.filters.status, v) })} />
              </>
            )}
            {/* §35: the id-valued filters, through pickers. Owner applies to every
                object; account to deals and contacts; contact and deal to
                activities — the server's allowlist (ID_FILTERS), never guessed. */}
            <div className="flex flex-wrap items-center gap-3 text-11 text-ink-muted" data-id-filters>
              <label className="inline-flex items-center gap-2">Owner
                <OwnerSelect value={def.filters.assignedEmployeeId ?? ''} onChange={id => setFilter({ assignedEmployeeId: id || undefined })}
                  className={`${inputCls} bg-surface-card`} includeLeavers emptyLabel="Any owner" />
              </label>
              {ID_FILTERS[object].includes('accountId') && (
                <IdFilter label="Account" htmlFor="report-filter-account" note={idNote.accountId}
                  active={Boolean(def.filters.accountId || idText.accountId)} onClear={() => setIdFilter('accountId', '', '')}>
                  <AccountPicker id="report-filter-account" className={`${inputCls} w-56`} allowCreate={false}
                    value={{ accountId: def.filters.accountId ?? '', company: idText.accountId ?? '' }}
                    onChange={n => setIdFilter('accountId', n.accountId, n.company)} />
                </IdFilter>
              )}
              {ID_FILTERS[object].includes('contactId') && (
                <IdFilter label="Contact" htmlFor="report-filter-contact" note={idNote.contactId}
                  active={Boolean(def.filters.contactId || idText.contactId)} onClear={() => setIdFilter('contactId', '', '')}>
                  <ContactPicker id="report-filter-contact" className={`${inputCls} w-56`}
                    value={{ contactId: def.filters.contactId ?? '', text: idText.contactId ?? '' }}
                    onChange={n => setIdFilter('contactId', n.contactId, n.text)} />
                </IdFilter>
              )}
              {ID_FILTERS[object].includes('dealId') && (
                <IdFilter label="Deal" htmlFor="report-filter-deal" note={idNote.dealId}
                  active={Boolean(def.filters.dealId || idText.dealId)} onClear={() => setIdFilter('dealId', '', '')}>
                  <DealPicker id="report-filter-deal" className={`${inputCls} w-64`}
                    value={{ dealId: def.filters.dealId ?? '', text: idText.dealId ?? '' }}
                    onChange={n => setIdFilter('dealId', n.dealId, n.text)} />
                </IdFilter>
              )}
            </div>
            {object !== 'activities' && (
              <label className="flex items-center gap-2 text-11 text-ink-muted">Source (exact text)
                <input type="text" value={def.filters.source?.[0] ?? ''} placeholder="e.g. referral" aria-label="Source filter"
                  onChange={e => setFilter({ source: e.target.value ? [e.target.value] : undefined })} className={`${inputCls} w-44`} />
              </label>
            )}
            <div className="flex flex-wrap items-center gap-2 text-11 text-ink-muted">
              <span className="uppercase tracking-wide">Date range</span>
              <select value={def.range?.field ?? ''} aria-label="Range field" className={`${inputCls} bg-surface-card`}
                onChange={e => set({ range: e.target.value ? { field: e.target.value, from: def.range?.from, to: def.range?.to } : undefined })}>
                <option value="">No date range</option>
                {RANGE_FIELDS[object].map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              {def.range && (
                <>
                  <input type="date" value={def.range.from ?? ''} aria-label="Range from" className={`${inputCls} w-40`}
                    onChange={e => set({ range: { ...def.range!, from: e.target.value || undefined } })} />
                  <span>to</span>
                  <input type="date" value={def.range.to ?? ''} aria-label="Range to" className={`${inputCls} w-40`}
                    onChange={e => set({ range: { ...def.range!, to: e.target.value || undefined } })} />
                  <span className="text-ink-faint">inclusive days, UTC</span>
                </>
              )}
            </div>
          </div>

          {/* Actions: run, save, load, export. */}
          <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-edge-row">
            <button type="button" className="k-btn disabled:opacity-60" disabled={running} onClick={() => void run()}>{running ? 'Running…' : 'Run report'}</button>
            <span className="inline-flex items-center gap-2">
              <input type="text" value={saveName} placeholder="Save as…" aria-label="Report name" className={`${inputCls} w-44`}
                onChange={e => setSaveName(e.target.value)} />
              <button type="button" className="k-btn-secondary disabled:opacity-60" disabled={saving || !saveName.trim()} onClick={() => void save()}>{saving ? 'Saving…' : 'Save'}</button>
            </span>
            {saved.length > 0 && (
              <select aria-label="Saved reports" value="" className={`${inputCls} bg-surface-card`} onChange={e => load(e.target.value)}>
                <option value="">Saved reports ({saved.length}, private to you)</option>
                {saved.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            )}
            <span className="inline-flex flex-col gap-0.5 ml-auto items-end">
              <button type="button" className="k-btn-secondary disabled:opacity-60" disabled={exporting} onClick={() => void exportCSV()}>{exporting ? 'Exporting…' : 'Export CSV'}</button>
              <span className="text-11 text-ink-faint max-w-72 text-right" data-export-note>{EXPORT_NOTE}</span>
            </span>
          </div>
        </div>
      </section>

      {/* 2. The answer. */}
      <section className="k-card" aria-label="Report result">
        <div className="k-card-head">
          <div>
            <div className="k-card-title">{result ? `${dimensionLabel(result.definition.object, result.definition.groupBy)} × ${measureLabel(result.definition.measure)}` : 'Result'}</div>
            <div className="k-card-sub">
              {result
                ? `${result.groupCount} ${result.groupCount === 1 ? 'group' : 'groups'} · ${result.totals.count} ${result.definition.object}${money_ && result.totals.measurePence !== null ? ` · total ${money(result.totals.measurePence / 100)}` : ''}`
                : 'Run the report to see its groups and totals'}
            </div>
          </div>
        </div>
        {running && <LoadingState />}
        {!running && !result && <EmptyState message="No result yet" hint="Choose the object, how to group it and what to measure, then run the report." />}
        {!running && result && <ResultView res={result} />}
      </section>
    </div>
  )
}

function ResultView({ res }: { res: SalesReportResult }) {
  const line = truncationLine(res)
  const money_ = isMoneyMeasure(res.definition.measure)
  const note = excludedNote(res.totals.excludedNonGbp)
  const bars = chartRows(res)
  // §24 C-2: a money measure takes the shared AXIS formatter (one unit per
  // axis from the top tick, exact ticks, mono) — chartRows are POUNDS. The
  // count measure keeps recharts' own ticks and a bare number.
  const axis = money_ ? moneyAxis(bars.map(b => b.value), { pence: false }) : null
  return (
    <>
      {line && <div className="px-5 py-2 text-12 text-ink-body border-b border-edge-row" data-truncated>{line}</div>}
      {note && <div className="px-5 py-2 text-12 text-ink-muted border-b border-edge-row">{note}</div>}
      {res.groups.length === 0 ? (
        <EmptyState message="No groups" hint="Nothing matched the filters and range — that is a result, not an error." />
      ) : (
        <>
          {res.definition.chart === 'bar' && (
            <div className="px-5 pt-4 font-mono" data-chart>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={bars}>
                  <CartesianGrid vertical={false} stroke={ink.faint} strokeOpacity={0.25} />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: ink.faint }} axisLine={false} tickLine={false} interval={0} />
                  {axis ? (
                    <YAxis tick={{ fontSize: 10, fill: ink.faint }} axisLine={false} tickLine={false} width={64}
                      ticks={axis.ticks} domain={axis.domain} tickFormatter={axis.format} />
                  ) : (
                    <YAxis tick={{ fontSize: 10, fill: ink.faint }} axisLine={false} tickLine={false} width={64}
                      tickFormatter={(v: number) => String(v)} />
                  )}
                  <Tooltip contentStyle={{ background: surface.card, border: 'none', borderRadius: 10, fontSize: 12, boxShadow: shadow.tooltip }}
                    formatter={(v: number) => (money_ ? money(v) : String(v))} cursor={{ fill: surface['row-hover'] }} />
                  <Bar dataKey="value" name={measureLabel(res.definition.measure)} fill={accent.DEFAULT} radius={[4, 4, 0, 0]} isAnimationActive={false} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          <table className="closr-table sales-table w-full">
            <thead>
              <tr>
                <th className="text-left">{dimensionLabel(res.definition.object, res.definition.groupBy)}</th>
                <th className="text-left">Count</th>
                {money_ && <th className="text-left">Counted</th>}
                {money_ && <th className="text-left">{measureLabel(res.definition.measure)}</th>}
                {money_ && <th className="text-left">Excluded (non-GBP)</th>}
              </tr>
            </thead>
            <tbody>
              {res.groups.map((g, i) => (
                <tr key={`${g.value ?? '∅'}-${i}`} data-group={g.value ?? ''}>
                  <td className={g.value === null ? 'text-13 text-ink-muted' : 'text-13 font-medium text-ink-primary'}>{groupLabel(res.definition.groupBy, g.value)}</td>
                  <td className="text-13 text-ink-body">{g.count}</td>
                  {money_ && <td className="text-13 text-ink-body">{countedCell(g)}</td>}
                  {money_ && <td className="text-13 font-semibold text-ink-primary">{measureCell(g.measurePence)}</td>}
                  {money_ && <td className="text-13 text-ink-muted">{g.excludedNonGbp ?? '—'}</td>}
                </tr>
              ))}
              <tr className="font-semibold" data-totals>
                <td className="text-13 text-ink-primary">{`All ${res.groupCount} ${res.groupCount === 1 ? 'group' : 'groups'}`}</td>
                <td className="text-13 text-ink-primary">{res.totals.count}</td>
                {money_ && <td className="text-13 text-ink-primary">{countedCell(res.totals)}</td>}
                {money_ && <td className="text-13 text-ink-primary">{measureCell(res.totals.measurePence)}</td>}
                {money_ && <td className="text-13 text-ink-primary">{res.totals.excludedNonGbp ?? '—'}</td>}
              </tr>
            </tbody>
          </table>
        </>
      )}
    </>
  )
}
